import React, { useEffect, useMemo, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { useMyAccessContext } from '@/hooks/security/useMyAccessContext';
import { toast } from 'sonner';
import { useNavigate, useLocation } from 'react-router-dom';
import { buildAdminRoute } from '@/constants/security';
import { authDebug } from '@/lib/authDebugInstrumentation';
import { createAdminNotificationsApi } from '@/types/adminNotifications/admin-notifications.api';
import { adminNotificationQueryKeys } from '@/types/adminNotifications/admin-notifications.query-keys';

type AgentRow = {
  id: string;
  message: string;
  route?: string | null;
  agent_user_id: string;
  created_at: string;
};

type AdminUserStateRow = {
  notification_id: number;
  user_id: string;
  created_at?: string | null;
};

// Global, lightweight listener to show a toast in real time when a new notification arrives.
// It intentionally avoids duplicating dashboard toasts by not firing on dashboard routes.
export const LiveNotificationToasts: React.FC = () => {
  const { user } = useAuth();
  const { accessContext } = useMyAccessContext({ enabled: !!user });
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const location = useLocation();
  const shownIdsRef = useRef<Set<string>>(new Set());

  const isAdmin = Boolean(accessContext?.isAdminOrSuperAdmin);

  // Suppress only on very specific pages that already display their own toasts
  // Tip: set this to false while testing to verify toasts anywhere
  const suppressOnThisRoute = useMemo(() => {
    const p = location.pathname;
    const suppressedPrefixes = [
      buildAdminRoute('notifications'),
      buildAdminRoute('visits'), // VisitBookingsManagement ya muestra confirmaciones locales
      buildAdminRoute('properties'), // PropertyManagement emite sus propios toasts tras cada acción
      '/agente/visitas',
    ];
    return suppressedPrefixes.some(prefix => p.startsWith(prefix));
  }, [location.pathname]);

  useEffect(() => {
    if (!user) return; // only when logged in

    const channels: any[] = [];
    const api = createAdminNotificationsApi(supabase);
    const uid = user.id;

    const showToast = (row: { id: string; title?: string; message: string; route?: string | null; created_at: string }) => {
      if (suppressOnThisRoute) return;
      if (shownIdsRef.current.has(row.id)) return;
      shownIdsRef.current.add(row.id);
      toast(row.title ?? row.message, {
        description: row.title ? row.message : undefined,
        duration: 3500,
        action: row.route
          ? {
              label: 'Ver',
              onClick: () => {
                const target = row.route!;
                if (/^https?:\/\//.test(target)) window.location.assign(target);
                else navigate(target);
              },
            }
          : undefined,
      });
    };

    try {
      // Admin notifications v2: listen to the per-user materialized state.
      // The canonical feed lives in admin_notification_events/user_states, not in
      // the legacy public.admin_notifications table.
      if (isAdmin) {
        const chAdminV2 = supabase
          .channel(`live_admin_notifications_v2_toasts_${uid}`)
          .on(
            'postgres_changes',
            {
              event: 'INSERT',
              schema: 'public',
              table: 'admin_notification_user_states',
              filter: `user_id=eq.${uid}`,
            },
            async (payload) => {
              const row = payload.new as AdminUserStateRow | null;
              const notificationId = Number(row?.notification_id);

              if (!row || row.user_id !== uid || !Number.isFinite(notificationId)) {
                return;
              }

              await Promise.all([
                queryClient.invalidateQueries({ queryKey: adminNotificationQueryKeys.feeds() }),
                queryClient.invalidateQueries({ queryKey: adminNotificationQueryKeys.unreadCount() }),
              ]);

              const feed = await api.getFeed({ limit: 10 });
              const item = feed.items.find((candidate) => candidate.id === notificationId);

              if (!item) {
                return;
              }

              showToast({
                id: `admin-v2:${item.id}`,
                title: item.title,
                message: item.body,
                route: item.href,
                created_at: item.createdAt,
              });
            },
          )
          .subscribe();
        channels.push(chAdminV2);
      }

      // Agent notifications, scoped to current user id.
      const chAgent = supabase
        .channel('live_agent_notifications_toasts')
        .on(
          'postgres_changes',
          { event: 'INSERT', schema: 'public', table: 'agent_notifications', filter: `agent_user_id=eq.${uid}` },
          (payload) => {
            const row = payload.new as AgentRow & { data?: Record<string, unknown> };
            if (!row || !row.id || row.agent_user_id !== uid) return;
            const route = row.route ?? (row as any)?.data?.route ?? null;
            showToast({ id: `agent:${row.id}`, message: row.message, route, created_at: row.created_at });
          }
        )
        .subscribe();
      channels.push(chAgent);
    } catch (error) {
      authDebug('LiveNotificationToasts.realtime_disabled', {
        reason: error instanceof Error ? error.message : 'realtime_init_failed',
      });
      console.warn('Realtime global notification toasts disabled safely', error);
    }

    return () => {
      channels.forEach((ch) => supabase.removeChannel(ch));
    };
  }, [user, user?.id, isAdmin, navigate, queryClient, suppressOnThisRoute]);

  return null;
};

export default LiveNotificationToasts;
